import React from 'react'
import { FiHeadphones, FiLogOut, FiSettings } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { AuthContext } from '../context/AuthContext'
import { FetchContext } from '../context/FetchContext'
import './Profile.css'

const getUserInfo = (authState) => authState?.userInfo?.dataValues || authState?.userInfo || {}

const getFullName = (profileData, userInfo) => {
    const parts = [
        profileData?.surname || userInfo?.surname,
        profileData?.name || userInfo?.username,
        profileData?.patronymic || userInfo?.patronymic,
    ].filter(Boolean)

    return parts.join(' ') || userInfo?.email || 'Пользователь'
}

const getInitial = (name) => {
    const firstLetter = String(name || '').trim().charAt(0)
    return firstLetter ? firstLetter.toUpperCase() : 'П'
}

const stringToHsl = (value) => {
    const text = String(value || 'Пользователь')
    let hash = 0

    for (let i = 0; i < text.length; i += 1) {
        hash = text.charCodeAt(i) + ((hash << 5) - hash)
    }

    const hue = Math.abs(hash % 360)
    return `hsl(${hue}, 42%, 76%)`
}

const formatDate = (value) => {
    if (!value) return 'Не указана'

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value

    return date.toLocaleDateString('ru-RU')
}

function Profile() {
    const fetchContext = React.useContext(FetchContext)
    const auth = React.useContext(AuthContext)
    const navigate = useNavigate()
    const userInfo = getUserInfo(auth.authState)
    const isAdmin = auth.isAdmin()
    const profileRef = React.useRef(null)

    const [profileData, setProfileData] = React.useState(null)
    const [isOpen, setIsOpen] = React.useState(false)

    const loadProfileData = React.useCallback(async () => {
        if (isAdmin) {
            setProfileData(null)
            return
        }

        try {
            const { data } = await fetchContext.authAxios.get('api/profile-data', { timeout: 5000 })

            if (data && !data.message) {
                setProfileData(data)
            }
        } catch (error) {
            console.log(error)
        }
    }, [fetchContext, isAdmin])

    React.useEffect(() => {
        loadProfileData()
    }, [loadProfileData])

    React.useEffect(() => {
        if (isAdmin) {
            setProfileData(null)
        }
    }, [isAdmin])

    React.useEffect(() => {
        if (!isOpen) return undefined

        const handlePointerDown = (event) => {
            if (!profileRef.current || profileRef.current.contains(event.target)) return
            setIsOpen(false)
        }

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false)
            }
        }

        document.addEventListener('pointerdown', handlePointerDown)
        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown)
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [isOpen])

    const visibleProfileData = isAdmin ? null : profileData
    const fullName = getFullName(visibleProfileData, userInfo)
    const regPlace = visibleProfileData?.RegPlace || userInfo?.RegPlace || 'Не указан'
    const birthdate = visibleProfileData?.birthdate || userInfo?.birthdate

    const handleSettings = () => {
        setIsOpen(false)
        navigate('/settings')
    }

    const handleSupport = () => {
        setIsOpen(false)
        navigate('/support')
    }

    const handleLogout = () => {
        setIsOpen(false)
        auth.logout()
    }

    return (
        <div ref={profileRef} className={`profileWidget${isOpen ? ' profileWidget--open' : ''}`}>
            <button
                type="button"
                className="profileWidget__summary"
                onClick={() => setIsOpen((current) => !current)}
                aria-expanded={isOpen}
            >
                <span
                    className="profileWidget__avatar"
                    style={{ backgroundColor: stringToHsl(fullName) }}
                >
                    {getInitial(fullName)}
                </span>
                <span className="profileWidget__name">{fullName}</span>
                <span className="profileWidget__chevron" />
            </button>

            {isOpen && (
                <div className="profileWidget__panel">
                    <div className="profileWidget__details">
                        <div>
                            <span>Дата рождения</span>
                            <strong>{formatDate(birthdate)}</strong>
                        </div>
                        <div>
                            <span>Адрес регистрации</span>
                            <strong>{regPlace}</strong>
                        </div>
                    </div>

                    <div className="profileWidget__actions">
                        {!isAdmin && (
                            <button type="button" onClick={handleSettings}>
                                <FiSettings />
                                <span>Настройки</span>
                            </button>
                        )}
                        {!isAdmin && (
                            <button type="button" onClick={handleSupport}>
                                <FiHeadphones />
                                <span>Поддержка</span>
                            </button>
                        )}
                        <button type="button" onClick={handleLogout}>
                            <FiLogOut />
                            <span>Выход</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default React.memo(Profile)
